import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../supabaseClient'
import EmptyState from '../ui/EmptyState'
import ErrorState from '../ui/ErrorState'
import FilterBar from '../ui/FilterBar'
import LoadingState from '../ui/LoadingState'
import SectionHeader from '../ui/SectionHeader'
import StatPill from '../ui/StatPill'
import StatusBadge from '../ui/StatusBadge'
import { ADMIN_ISSUE_CATEGORIES, ADMIN_ISSUE_STATUSES } from './adminConfig'
import { formatPlainText, formatTitleText } from '../utils/displayText'
import { buildSchemaUnavailableMessage, isSchemaMismatchError } from './schemaGuard'

const createBlankForm = () => ({
  issue_category: 'attendance_issue',
  title: '',
  related_worker_id: '',
  related_job_id: '',
  related_site_id: '',
  owner_label: '',
  assignee_worker_id: '',
  resolution_status: 'open',
  due_date: '',
  notes: '',
  attachments: ''
})

const formatDateTime = (value) => {
  if (!value) return 'Unknown'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Unknown'
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

const mapIssueRow = (row) => ({
  ...row,
  issue_category: String(row.issue_category || 'attendance_issue').toLowerCase(),
  resolution_status: String(row.resolution_status || 'open').toLowerCase()
})

const toFormFromIssue = (issue) => ({
  issue_category: issue.issue_category || 'attendance_issue',
  title: issue.title || '',
  related_worker_id: issue.related_worker_id ? String(issue.related_worker_id) : '',
  related_job_id: issue.related_job_id ? String(issue.related_job_id) : '',
  related_site_id: issue.related_site_id ? String(issue.related_site_id) : '',
  owner_label: issue.owner_label || '',
  assignee_worker_id: issue.assignee_worker_id ? String(issue.assignee_worker_id) : '',
  resolution_status: issue.resolution_status || 'open',
  due_date: issue.due_date || '',
  notes: issue.notes || '',
  attachments: Array.isArray(issue.attachments) ? issue.attachments.join(', ') : issue.attachments || ''
})

export default function AdminIssuesPanel({ onDataChanged }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [issuesUnavailableMessage, setIssuesUnavailableMessage] = useState('')

  const [workers, setWorkers] = useState([])
  const [jobs, setJobs] = useState([])
  const [sites, setSites] = useState([])
  const [issues, setIssues] = useState([])

  const [editingIssueId, setEditingIssueId] = useState('')
  const [showUnresolvedOnly, setShowUnresolvedOnly] = useState(true)
  const [statusFilter, setStatusFilter] = useState('all')
  const [form, setForm] = useState(createBlankForm)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    setIssuesUnavailableMessage('')

    const [workersResponse, jobsResponse, sitesResponse, issuesResponse] = await Promise.all([
      supabase.from('workers').select('id, name').order('name'),
      supabase.from('jobs').select('id, job_type, job_date').order('job_date', { ascending: false }).limit(200),
      supabase.from('sites').select('id, name').order('name'),
      supabase.from('admin_issues').select('*').order('updated_at', { ascending: false }).limit(300)
    ])

    const issuesSchemaMismatch = isSchemaMismatchError(issuesResponse.error)

    if (workersResponse.error || jobsResponse.error || sitesResponse.error || (issuesResponse.error && !issuesSchemaMismatch)) {
      setWorkers([])
      setJobs([])
      setSites([])
      setIssues([])
      setError(
        workersResponse.error?.message ||
          jobsResponse.error?.message ||
          sitesResponse.error?.message ||
          issuesResponse.error?.message ||
          'Unable to load issue center.'
      )
      setLoading(false)
      return
    }

    setWorkers(workersResponse.data || [])
    setJobs(jobsResponse.data || [])
    setSites(sitesResponse.data || [])
    if (issuesSchemaMismatch) {
      setIssuesUnavailableMessage(buildSchemaUnavailableMessage('Issue center', 'Correction Center'))
      setIssues([])
    } else {
      setIssues((issuesResponse.data || []).map(mapIssueRow))
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const workerById = useMemo(() => new Map(workers.map((worker) => [String(worker.id), worker])), [workers])
  const jobById = useMemo(() => new Map(jobs.map((job) => [String(job.id), job])), [jobs])
  const siteById = useMemo(() => new Map(sites.map((site) => [String(site.id), site])), [sites])

  const visibleIssues = useMemo(() => {
    return issues.filter((issue) => {
      if (showUnresolvedOnly && ['resolved', 'closed'].includes(issue.resolution_status)) return false
      if (statusFilter !== 'all' && issue.resolution_status !== statusFilter) return false
      return true
    })
  }, [issues, showUnresolvedOnly, statusFilter])

  const stats = useMemo(() => {
    const unresolvedCount = issues.filter((issue) => !['resolved', 'closed'].includes(issue.resolution_status)).length
    const blockedCount = issues.filter((issue) => issue.resolution_status === 'blocked').length
    const overdueCount = issues.filter((issue) => {
      if (!issue.due_date) return false
      if (['resolved', 'closed'].includes(issue.resolution_status)) return false
      return issue.due_date < new Date().toISOString().slice(0, 10)
    }).length
    return { unresolvedCount, blockedCount, overdueCount }
  }, [issues])

  const handleChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value
    }))
  }

  const handleEdit = (issue) => {
    setEditingIssueId(String(issue.id))
    setForm(toFormFromIssue(issue))
    setMessage('')
  }

  const resetForm = () => {
    setEditingIssueId('')
    setForm(createBlankForm())
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (issuesUnavailableMessage) {
      setMessage(issuesUnavailableMessage)
      return
    }

    const title = formatTitleText(form.title)
    if (!title) {
      setMessage('Issue title is required.')
      return
    }

    setSaving(true)
    setMessage('')

    const payload = {
      issue_category: String(form.issue_category || 'attendance_issue').toLowerCase(),
      title,
      related_worker_id: form.related_worker_id || null,
      related_job_id: form.related_job_id || null,
      related_site_id: form.related_site_id || null,
      owner_label: formatTitleText(form.owner_label),
      assignee_worker_id: form.assignee_worker_id || null,
      resolution_status: String(form.resolution_status || 'open').toLowerCase(),
      due_date: form.due_date || null,
      notes: formatPlainText(form.notes),
      attachments: formatPlainText(form.attachments)
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    }

    let response
    if (editingIssueId) {
      response = await supabase.from('admin_issues').update(payload).eq('id', editingIssueId)
    } else {
      response = await supabase.from('admin_issues').insert([payload])
    }

    if (response.error) {
      setSaving(false)
      setMessage('Unable to save issue: ' + response.error.message)
      return
    }

    setSaving(false)
    setMessage(editingIssueId ? 'Issue updated.' : 'Issue created.')
    resetForm()
    await load()
    onDataChanged?.()
  }

  const handleStatusChange = async (issue, nextStatus) => {
    if (issuesUnavailableMessage) {
      setMessage(issuesUnavailableMessage)
      return
    }

    setSaving(true)
    setMessage('')

    const response = await supabase
      .from('admin_issues')
      .update({
        resolution_status: nextStatus,
        resolved_at: ['resolved', 'closed'].includes(nextStatus) ? new Date().toISOString() : null
      })
      .eq('id', issue.id)

    if (response.error) {
      setSaving(false)
      setMessage('Unable to update issue status: ' + response.error.message)
      return
    }

    setSaving(false)
    setMessage('Issue status updated.')
    await load()
    onDataChanged?.()
  }

  if (loading) {
    return <LoadingState title="Loading issue center" message="Preparing issue records and assignment context." />
  }

  if (error) {
    return <ErrorState title="Issue center unavailable" message={error} />
  }

  return (
    <section className="gg-module-stack">
      <section className="gg-module-panel">
        <SectionHeader
          eyebrow="Admin"
          title="Issue / Incident Center"
          description="Track attendance/site/pay/conduct/safety/equipment/client-vendor issues with assignee ownership."
        />
        <div className="gg-tool-pills">
          <StatPill label="Unresolved" value={stats.unresolvedCount} tone={stats.unresolvedCount ? 'warning' : 'info'} />
          <StatPill label="Blocked" value={stats.blockedCount} tone={stats.blockedCount ? 'error' : 'info'} />
          <StatPill label="Overdue" value={stats.overdueCount} tone={stats.overdueCount ? 'warning' : 'info'} />
        </div>
        {issuesUnavailableMessage ? <p className="gg-inline-alert gg-inline-alert--warning">{issuesUnavailableMessage}</p> : null}
      </section>

      <section className="gg-module-panel">
        <p className="gg-module-panel__label">{editingIssueId ? 'Edit Issue' : 'Create Issue'}</p>
        {issuesUnavailableMessage ? (
          <EmptyState
            title="Issue records unavailable"
            message="Issue tracking tables are not enabled in this environment yet. Use correction and workforce panels for live testing."
          />
        ) : (
          <form className="gg-form-grid" onSubmit={handleSubmit}>
            <label className="gg-form-field">
              <span>Category</span>
              <select className="gg-select" value={form.issue_category} onChange={(event) => handleChange('issue_category', event.target.value)}>
                {ADMIN_ISSUE_CATEGORIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="gg-form-field">
              <span>Issue Title *</span>
              <input className="gg-input" value={form.title} onChange={(event) => handleChange('title', event.target.value)} />
            </label>
            <label className="gg-form-field">
              <span>Related Worker</span>
              <select className="gg-select" value={form.related_worker_id} onChange={(event) => handleChange('related_worker_id', event.target.value)}>
                <option value="">Not linked</option>
                {workers.map((worker) => (
                  <option key={worker.id} value={worker.id}>
                    {formatTitleText(worker.name, 'Worker')}
                  </option>
                ))}
              </select>
            </label>
            <label className="gg-form-field">
              <span>Related Job</span>
              <select className="gg-select" value={form.related_job_id} onChange={(event) => handleChange('related_job_id', event.target.value)}>
                <option value="">Not linked</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {formatTitleText(job.job_type, 'Job')} • {job.job_date || 'No date'}
                  </option>
                ))}
              </select>
            </label>
            <label className="gg-form-field">
              <span>Related Site</span>
              <select className="gg-select" value={form.related_site_id} onChange={(event) => handleChange('related_site_id', event.target.value)}>
                <option value="">Not linked</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {formatTitleText(site.name, 'Site')}
                  </option>
                ))}
              </select>
            </label>
            <label className="gg-form-field">
              <span>Owner / Team</span>
              <input className="gg-input" value={form.owner_label} onChange={(event) => handleChange('owner_label', event.target.value)} placeholder="Operations" />
            </label>
            <label className="gg-form-field">
              <span>Assignee</span>
              <select className="gg-select" value={form.assignee_worker_id} onChange={(event) => handleChange('assignee_worker_id', event.target.value)}>
                <option value="">Unassigned</option>
                {workers.map((worker) => (
                  <option key={worker.id} value={worker.id}>
                    {formatTitleText(worker.name, 'Worker')}
                  </option>
                ))}
              </select>
            </label>
            <label className="gg-form-field">
              <span>Status</span>
              <select className="gg-select" value={form.resolution_status} onChange={(event) => handleChange('resolution_status', event.target.value)}>
                {ADMIN_ISSUE_STATUSES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="gg-form-field">
              <span>Due Date</span>
              <input className="gg-input" type="date" value={form.due_date} onChange={(event) => handleChange('due_date', event.target.value)} />
            </label>
            <label className="gg-form-field">
              <span>Notes</span>
              <textarea className="gg-textarea" value={form.notes} onChange={(event) => handleChange('notes', event.target.value)} />
            </label>
            <label className="gg-form-field">
              <span>Attachments (comma-separated labels/URLs)</span>
              <textarea className="gg-textarea" value={form.attachments} onChange={(event) => handleChange('attachments', event.target.value)} />
            </label>
            <div className="gg-form-actions">
              <button type="submit" className="gg-button gg-button--primary" disabled={saving}>
                {saving ? 'Saving...' : editingIssueId ? 'Save Issue' : 'Create Issue'}
              </button>
              {editingIssueId ? (
                <button type="button" className="gg-button gg-button--ghost" onClick={resetForm}>
                  Cancel Edit
                </button>
              ) : null}
            </div>
          </form>
        )}
        {message ? (
          <p
            className={`gg-inline-alert ${
              message.toLowerCase().includes('unable')
                ? 'gg-inline-alert--error'
                : message.toLowerCase().includes('unavailable')
                  ? 'gg-inline-alert--warning'
                  : 'gg-inline-alert--success'
            }`}
          >
            {message}
          </p>
        ) : null}
      </section>

      <section className="gg-module-panel">
        {issuesUnavailableMessage ? (
          <EmptyState title="Issue center unavailable" message="Issue table is not present in this environment." />
        ) : (
          <>
            <FilterBar
              left={
                <label className="gg-form-field">
                  <span>Status Filter</span>
                  <select className="gg-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                    <option value="all">All</option>
                    {ADMIN_ISSUE_STATUSES.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              }
              right={
                <label className="gg-form-field">
                  <span>Unresolved only</span>
                  <input type="checkbox" checked={showUnresolvedOnly} onChange={(event) => setShowUnresolvedOnly(event.target.checked)} />
                </label>
              }
              summary={`${visibleIssues.length} issues shown`}
            />

            {visibleIssues.length === 0 ? (
              <EmptyState title="No issues in view" message="Adjust filters or create a new issue." />
            ) : (
              <div className="gg-record-list">
                {visibleIssues.map((issue) => {
                  const linkedWorker = issue.related_worker_id ? workerById.get(String(issue.related_worker_id)) : null
                  const linkedJob = issue.related_job_id ? jobById.get(String(issue.related_job_id)) : null
                  const linkedSite = issue.related_site_id ? siteById.get(String(issue.related_site_id)) : null
                  const overdue = issue.due_date && issue.due_date < new Date().toISOString().slice(0, 10) && !['resolved', 'closed'].includes(issue.resolution_status)

                  return (
                    <article key={issue.id} className="gg-record-item">
                      <div className="gg-record-item__head">
                        <div>
                          <p className="gg-record-item__title">{formatTitleText(issue.title, 'Issue')}</p>
                          <p className="gg-record-item__meta">
                            {formatTitleText(issue.issue_category, 'Issue')} • Owner {formatTitleText(issue.owner_label, 'Unassigned')}
                          </p>
                          <p className="gg-record-item__meta">
                            Worker {formatTitleText(linkedWorker?.name, 'Not linked')} • Job {formatTitleText(linkedJob?.job_type, linkedJob?.id || 'Not linked')} • Site {formatTitleText(linkedSite?.name, 'Not linked')}
                          </p>
                          <p className="gg-record-item__meta">Due {issue.due_date || 'Not set'} • Updated {formatDateTime(issue.updated_at || issue.created_at)}</p>
                          {issue.notes ? <p className="gg-record-item__meta">{issue.notes}</p> : null}
                        </div>
                        <div className="gg-form-actions">
                          <StatusBadge
                            status={
                              issue.resolution_status === 'resolved' || issue.resolution_status === 'closed'
                                ? 'success'
                                : issue.resolution_status === 'blocked'
                                  ? 'error'
                                  : 'warning'
                            }
                            label={formatTitleText(issue.resolution_status, 'Open')}
                          />
                          {overdue ? <span className="gg-inline-chip gg-inline-chip--warning">Overdue</span> : null}
                        </div>
                      </div>
                      <div className="gg-form-actions">
                        <button type="button" className="gg-button gg-button--ghost" onClick={() => handleEdit(issue)} disabled={saving}>
                          Edit
                        </button>
                        {ADMIN_ISSUE_STATUSES.map((statusOption) => (
                          <button
                            key={`${issue.id}:${statusOption.value}`}
                            type="button"
                            className="gg-button gg-button--ghost"
                            onClick={() => handleStatusChange(issue, statusOption.value)}
                            disabled={saving || issue.resolution_status === statusOption.value}
                          >
                            {statusOption.label}
                          </button>
                        ))}
                      </div>
                    </article>
                  )
                })}
              </div>
            )}
          </>
        )}
      </section>
    </section>
  )
}
